import React from "react";
// Service Provider page

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Avatar from "@mui/material/Avatar";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomDataTable from "../table/CustomTable";
import PriceDistributionBarChart from "../charts/PriceDistributionBarChart";
import RevenueBreakdownPieChart from "../charts/RevenueBreakdownPieChart";

// React icons
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import { MdOutlineProductionQuantityLimits } from "react-icons/md";
import { FaChartLine } from "react-icons/fa";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

const ServiceProvider: React.FC = () => {
  const activeBuyColumns = [
    "Buyer ID",
    "Requested Energy",
    "Bid Price",
    "Total Bid Value",
    "Time of Order",
  ];

  const activeSellColumns = [
    "Seller ID",
    "Offered Energy",
    "Ask Price",
    "Total Ask Value",
    "Time of Order",
  ];

  const recentTransactionColumns = [
    "Buyer ID",
    "Seller ID",
    "Traded Energy",
    "Price",
    "Total Bid Value",
    "Total Value",
    "Timestamp",
  ];

  const activeBuyOrderData = [
    {
      buyerId: "Buyer123",
      reqEnergy: "100 kWh",
      bidPrice: "₹4.50/kWh",
      totalBidValue: "₹450",
      timeOfOrder: "15:08:2024 10:15:30",
    },
  ];

  const activeSellOrderData = [
    {
      sellerId: "Seller789",
      offeredEnergy: "200 kWh",
      askPrice: "₹4.75/kWh",
      totalAskValue: "₹950",
      timeOfOrder: "115:08:2024 10:10:45",
    },
  ];

  const recentTransactionData = [
    {
      buyerId: "B123",
      sellerId: "S456",
      tradedEnergy: "100kWh",
      price: "$0.08/kWh",
      totalBidValue: "$8.00",
      totalValue: "$8.00",
      timeStamp: "2024-08-19 10:30:00",
    },
    {
      buyerId: "B123",
      sellerId: "S456",
      tradedEnergy: "100kWh",
      price: "$0.08/kWh",
      totalBidValue: "$8.00",
      totalValue: "$8.00",
      timeStamp: "2024-08-19 10:30:00",
    },
    {
      buyerId: "B123",
      sellerId: "S456",
      tradedEnergy: "100kWh",
      price: "$0.08/kWh",
      totalBidValue: "$8.00",
      totalValue: "$8.00",
      timeStamp: "2024-08-19 10:30:00",
    },
    {
      buyerId: "B123",
      sellerId: "S456",
      tradedEnergy: "100kWh",
      price: "$0.08/kWh",
      totalBidValue: "$8.00",
      totalValue: "$8.00",
      timeStamp: "2024-08-19 10:30:00",
    },
  ];

  const serviceProviderData = [
    {
      title: "Total Energy Transacted",
      energy: "5,00,000",
      increament: "12.08",
      description: "Total energy traded through the platform this month.",
    },
    {
      title: "Total Transaction Fees",
      money: "1,500,000",
      increament: "08.12",
      description: "Revenue generated from transaction fees on energy trades.",
    },
    {
      title: "Active Participants",
      users: "1200",
      increament: "08.12",
      description: "Number of participants currently trading on the platform.",
    },
  ];

  const rowsActiveBuyOrderContent = activeBuyOrderData?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
            <Avatar alt={item.buyerId} sx={{ width: "30px", height: "30px" }} />
            {item.buyerId}
          </Stack>
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.reqEnergy}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.bidPrice}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.totalBidValue}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.timeOfOrder}
        </Typography>
      </TableCell>
    </TableRow>
  ));

  const rowsActiveSellerOrderContent = activeSellOrderData?.map(
    (item, index) => (
      <TableRow
        key={index}
        sx={{
          height: "50px",
          background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
        }}
        className="custom_table_tbody"
      >
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
              <Avatar
                alt={item.sellerId}
                sx={{ width: "30px", height: "30px" }}
              />
              {item.sellerId}
            </Stack>
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item.offeredEnergy}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item.askPrice}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item.totalAskValue}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item.timeOfOrder}
          </Typography>
        </TableCell>
      </TableRow>
    ),
  );

  const rowsRecentTransactionsContent = recentTransactionData?.map(
    (item, index) => (
      <TableRow
        key={index}
        sx={{
          height: "50px",
          background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
        }}
        className="custom_table_tbody"
      >
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
              <Avatar
                alt={item.sellerId}
                sx={{ width: "30px", height: "30px" }}
              />
              {item.buyerId}
            </Stack>
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
              <Avatar
                alt={item.sellerId}
                sx={{ width: "30px", height: "30px" }}
              />
              {item.sellerId}
            </Stack>
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item.tradedEnergy}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item.price}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item.totalBidValue}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item.totalValue}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item.timeStamp}
          </Typography>
        </TableCell>
      </TableRow>
    ),
  );

  return (
    <Layout>
      <Card>
        <CardHeader
          title={<Typography variant="h5">Service Provider</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            {serviceProviderData?.map((item, index) => {
              return (
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <Card
                    sx={{
                      border: "1px solid #D7DAE7",
                      padding: "16px",
                      background: index === 0 ? "#0396FF" : "transparent",
                      height: 207,
                    }}
                  >
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={"10px"}
                      >
                        <Box
                          sx={{
                            background: index === 0 ? "#28A6FF" : "#D7DAE7",
                            width: "fit-content",
                            padding: "10px 10px 3px 10px",
                            borderRadius: "5px",
                          }}
                        >
                          <UnifiedIcon
                            icon={MdOutlineProductionQuantityLimits}
                            color={index === 0 ? "#ffffff" : "#999b9d"}
                            fontSize="30px"
                          />
                        </Box>

                        <Typography
                          variant="h5"
                          color={index === 0 ? "#ffffff" : "#000000"}
                          fontWeight={500}
                          lineHeight={"25px"}
                        >
                          {item.title}
                        </Typography>
                      </Stack>

                      <UnifiedIcon
                        icon={HiOutlineDotsHorizontal}
                        color={index === 0 ? "#ffffff" : "#000000"}
                        fontSize="25px"
                      />
                    </Stack>

                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      padding={"0 20px"}
                      mt={"20px"}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={500}
                        fontSize={"25px"}
                        color={index === 0 ? "#ffffff" : "#000000"}
                      >
                        {" "}
                        {item.money && <span>&#8377;</span>}{" "}
                        {item.users || item.energy || item.money}{" "}
                        {item.energy && (
                          <Typography fontSize={"12px"} fontWeight={400}>
                            kWh
                          </Typography>
                        )}{" "}
                      </Typography>

                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={"7px"}
                      >
                        <UnifiedIcon
                          icon={FaChartLine}
                          color={index === 0 ? "#ffffff" : "#999B9D"}
                          fontSize="20px"
                        />
                        <Typography
                          variant="body1"
                          color={index === 0 ? "#ffffff" : "#000000"}
                        >
                          +{item.increament}%
                        </Typography>
                      </Stack>
                    </Stack>

                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      mt={"20px"}
                    >
                      <Typography
                        variant="body1"
                        color={index === 0 ? "#ffffff" : "#000000"}
                        maxWidth={"180px"}
                        lineHeight={"20px"}
                      >
                        {item.description}
                      </Typography>
                      <ContainedButton
                        label={"Last Month"}
                        bgColor={
                          index === 0
                            ? "#ffffff"
                            : "linear-gradient(180deg, #1976d2 0%, #0396FF 100%)"
                        }
                        color={index === 0 ? "#0396FF" : "#ffffff"}
                        hoverBgColor={
                          index === 0
                            ? "#ffffff"
                            : "linear-gradient(180deg, #75D3FB 0%, #28A6FF 100%)"
                        }
                        sx={{
                          width: "fit-content",
                        }}
                      />
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader title={<Typography variant="h5">Order Book</Typography>} />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="body1" fontWeight={500}>
                      Active Buy Orders
                    </Typography>
                  }
                />
                <Divider />
                <CardContent sx={{ maxHeight: "350px" }}>
                  <CustomDataTable
                    sx={{ maxHeight: "300px" }}
                    columns={activeBuyColumns}
                    pagination={false}
                    rowsContent={rowsActiveBuyOrderContent}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="body1" fontWeight={500}>
                      Active Sell Orders
                    </Typography>
                  }
                />
                <Divider />
                <CardContent sx={{ maxHeight: "350px" }}>
                  <CustomDataTable
                    sx={{ maxHeight: "300px" }}
                    columns={activeSellColumns}
                    pagination={false}
                    rowsContent={rowsActiveSellerOrderContent}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                }}
              >
                <CardContent sx={{ maxHeight: "350px" }}>
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Stack direction={"row"} alignItems={"center"} gap={"20px"}>
                      <Box
                        sx={{
                          background: "#D7DAE7",
                          width: "fit-content",
                          padding: "10px 10px 3px 10px",
                          borderRadius: "5px",
                        }}
                      >
                        <UnifiedIcon
                          icon={MdOutlineProductionQuantityLimits}
                          color="#999b9d"
                          fontSize="30px"
                        />
                      </Box>

                      <Typography
                        variant="h5"
                        fontWeight={500}
                        lineHeight={"25px"}
                      >
                        Price Matching Overview
                      </Typography>
                    </Stack>

                    <UnifiedIcon
                      icon={HiOutlineDotsHorizontal}
                      color="#000000"
                      fontSize="25px"
                    />
                  </Stack>

                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    mt={"20px"}
                  >
                    <Stack>
                      <Typography variant="body2" fontSize={"13px"}>
                        Matched Volume
                      </Typography>
                      <Typography variant="body1" fontWeight={600} mt={"5px"}>
                        250 kWh traded
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant="body2" fontSize={"13px"}>
                        Current Market Price:
                      </Typography>
                      <Typography variant="body1" fontWeight={600} mt={"5px"}>
                        ₹4.60/kWh
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant="body2" fontSize={"13px"}>
                        Spread:
                      </Typography>
                      <Typography variant="body1" fontWeight={600} mt={"5px"}>
                        ₹0.25
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="body1" fontWeight={500}>
                      Price Distribution
                    </Typography>
                  }
                />
                <Divider />
                <CardContent sx={{ maxHeight: "350px", width: "100%" }}>
                  <PriceDistributionBarChart />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">Financial Overview</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                }}
              >
                <CardContent sx={{ maxHeight: "350px" }}>
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Stack direction={"row"} alignItems={"center"} gap={"20px"}>
                      <Box
                        sx={{
                          background: "#D7DAE7",
                          width: "fit-content",
                          padding: "10px 10px 3px 10px",
                          borderRadius: "5px",
                        }}
                      >
                        <UnifiedIcon
                          icon={MdOutlineProductionQuantityLimits}
                          color="#999b9d"
                          fontSize="30px"
                        />
                      </Box>

                      <Typography
                        variant="h5"
                        fontWeight={500}
                        lineHeight={"25px"}
                      >
                        Monthly Revenue
                      </Typography>
                    </Stack>

                    <UnifiedIcon
                      icon={HiOutlineDotsHorizontal}
                      color="#000000"
                      fontSize="25px"
                    />
                  </Stack>

                  <Stack mt={"30px"}>
                    <Typography variant="h5" fontWeight={500} fontSize={"22px"}>
                      ₹5,000,000
                    </Typography>
                    <Typography variant="body1" mt={"10px"}>
                      Revenue collected from all transactions this month
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="body1" fontWeight={500}>
                      Revenue Breakdown
                    </Typography>
                  }
                />
                <Divider />

                <CardContent sx={{ maxHeight: "350px", width: "100%" }}>
                  <RevenueBreakdownPieChart />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">Recent Transactions</Typography>}
        />
        <Divider />

        <CardContent>
          <CustomDataTable
            sx={{ maxHeight: "400px" }}
            columns={recentTransactionColumns}
            pagination={false}
            rowsContent={rowsRecentTransactionsContent}
          />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default ServiceProvider;
